import { useState, useCallback, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { activity } from "../services/api";
import type { ActivityQueryResult } from "../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }) +
    " " +
    d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
}

function actionTypeBadge(actionType: string) {
  const lower = actionType.toLowerCase();
  if (lower.includes("upload") && !lower.includes("rate")) {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
        Upload
      </span>
    );
  }
  if (lower.includes("rate")) {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
        Rate Card Change
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
      {actionType}
    </span>
  );
}

// ─── Action type options ──────────────────────────────────────────────────────

const ACTION_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "UPLOAD", label: "Upload" },
  { value: "RATE_CARD_UPLOAD", label: "Rate Card Upload" },
  { value: "WAREHOUSE_RATE_CHANGE", label: "Warehouse Rate Change" },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ActivityPage() {
  // Filter state
  const [userFilter, setUserFilter] = useState("");
  const [actionTypeFilter, setActionTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Applied filters (only sent to API when "Apply" is clicked)
  const [appliedFilters, setAppliedFilters] = useState<{
    user?: string;
    action_type?: string;
    date_from?: string;
    date_to?: string;
  }>({});

  // Pagination (page-based)
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error, isFetching } = useQuery<ActivityQueryResult>({
    queryKey: ["activity", appliedFilters, currentPage],
    queryFn: () =>
      activity.list({
        ...appliedFilters,
        page: currentPage,
      }),
  });

  const entries = data?.activities ?? [];
  const hasMore = data?.has_more ?? false;

  // All entries accumulated across pages for the "feed" display
  // (we re-fetch per page, not accumulate, since TanStack Query caches each page)

  const handleApplyFilters = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setCurrentPage(1);
      setAppliedFilters({
        user: userFilter.trim() || undefined,
        action_type: actionTypeFilter || undefined,
        date_from: fromDate || undefined,
        date_to: toDate || undefined,
      });
    },
    [userFilter, actionTypeFilter, fromDate, toDate],
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
        <p className="mt-1 text-sm text-gray-600">
          Recent actions and audit trail.
        </p>
      </div>

      {/* ── Filters ── */}
      <form
        onSubmit={handleApplyFilters}
        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              User
            </label>
            <input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Email or user ID"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Action Type
            </label>
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            type="submit"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </form>

      {/* ── Error ── */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load activity log. Please try refreshing the page.
        </div>
      )}

      {/* ── Loading (initial) ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="h-6 w-6 animate-spin text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="ml-2 text-sm text-gray-500">
            Loading activity...
          </span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && !error && entries.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-4 text-sm font-medium text-gray-900">
            No activity found
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your filters or check back later.
          </p>
        </div>
      )}

      {/* ── Activity Feed ── */}
      {entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="rounded-xl border border-gray-200 bg-white px-6 py-4 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium text-gray-900">
                      {entry.details.description}
                    </p>
                    {actionTypeBadge(entry.action_type)}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {entry.user_email}
                  </p>
                </div>
                <p className="ml-4 shrink-0 text-xs text-gray-500">
                  {formatTimestamp(entry.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {/* ── Load More ── */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={isFetching}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                {isFetching ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
